import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {AuthService} from '../../../shared/services/auth.service';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Router} from '@angular/router';
import {takeUntil} from 'rxjs/operators';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import {DeregistrationPack} from '../../../shared/interface/deregistration-pack';
import {DeregistrationPackService} from '../../../shared/services/deregistration-pack.service';
import {HttpErrorResponse} from '@angular/common/http';
import {
  DEREGISTRATION_PACK_STATUS_SENT,
  DICT_TYPE_TEMPLATE_TYPE, DictService, TEMPLATE_TYPE_DECLARATION_FOR_DEREGISTRATION,
  TEMPLATE_TYPE_PARTNER_DATA
} from "../../../shared/services/dict.service";
import {Uuid} from "../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../shared/components/job-result-dialog/job-result-dialog.component";
import {TemplateService} from "../../../shared/services/template.service";

@Component({
  selector: 'app-deregistration-pack-details-view',
  templateUrl: './deregistration-pack-details-view.component.html',
  styleUrls: ['./deregistration-pack-details-view.component.scss']
})
export class DeregistrationPackDetailsViewComponent implements OnInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  readonly DEREGISTRATION_PACK_STATUS_SENT = DEREGISTRATION_PACK_STATUS_SENT;

  @Input() deregistrationPack: Observable<DeregistrationPack>;
  @Output() modifyClicked = new EventEmitter<any>();

  deregistrationPackObject?: DeregistrationPack;
  userPermissions: ReplaySubject<UserPermissions>;
  processingRequest = false;
  generatingDocument = false;

  constructor(
    private auth: AuthService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private deregistrationPackService: DeregistrationPackService,
    private router: Router,
    private dictService: DictService,
    private templateService: TemplateService,
  ) {
    this.userPermissions = auth.getUserPermissions();
  }

  ngOnInit(): void {
    this.deregistrationPack
      .pipe(takeUntil(this.destroyed$))
      .subscribe((deregistrationPack: DeregistrationPack) => this.deregistrationPackObject = deregistrationPack);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  modify(): void {
    this.modifyClicked.emit();
  }

  delete(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć paczkę?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deregistrationPackService.deleteDeregistrationPack(this.deregistrationPackObject.uuid)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto paczkę', '', {duration: 2000});
            this.router.navigate(['/wyrejestrowania']);
          }, (error: HttpErrorResponse) => {
            this.snack.open('Podczas usuwania obiektu wystąpił błąd', error.status.toString(), {
              duration: 2000,
            });

            if (error.status === 400) {
              this.snack.open(error.error.error.text, error.status.toString(), {
                duration: 3000,
              });
            }
          });
      }
    });
  }

  confirmDeregistration(): void {
    this.processingRequest = true;

    this.deregistrationPackService.confirmDeregistration(this.deregistrationPackObject.uuid)
      .subscribe(() => {
        this.processingRequest = false;
        this.snack.open('Rejestracje zostały zaktualizowane', '', {duration: 2000});
      }, (error: HttpErrorResponse) => {
        this.processingRequest = false;
        this.snack.open('Przy aktualizowaniu rejestracji wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  generatePartnerDataDocuments(): void {
    this.generatingDocument = true;
    this.dictService.getId(TEMPLATE_TYPE_DECLARATION_FOR_DEREGISTRATION, DICT_TYPE_TEMPLATE_TYPE)
      .subscribe((dictId: number) => {
        this.templateService.generate({
          type: dictId,
          deregistrationPackIds: [this.deregistrationPackObject.uuid],
        }).subscribe((response: Uuid) => {
          const dialogRef = this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: {jobId: response.uuid, showProgress: false},
          });

          dialogRef.afterClosed().subscribe(() => {
            this.generatingDocument = false;
          });
        }, (error: HttpErrorResponse) => {
          this.generatingDocument = false;
          if (error.status === 404) {
            this.snack.open('Brak szablonów dla tego typu dokumentu', '', {duration: 2000});
            return;
          }

          this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
        });
      });
  }
}
